import React from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { useHistory } from "react-router-dom";
import useClickOutside from "hooks/useClickOutside";

import { IOffers } from "features/offers/offers.interface";
import { OFFER_MAPPING } from "features/offers/offers.config";
import { formattedTitle, handleShareOffer, routeToOfferDetails } from "features/offers/offers-utils";
import Timer from "../timer/Timer";
import defaultImg from "assets/images/offers/offer2.png";
import close from "assets/images/modal-close.png";
import { useAppDispatch } from "store/store";

import "./OfferDetailModal.scss";

export interface IOfferDetailModalProps {
  offer: IOffers;
  isOpen: boolean;
  onClose: () => void;
  isOfferListing?: boolean;
}

const OfferDetailModal: React.FC<IOfferDetailModalProps> = ({
  offer,
  isOpen,
  onClose,
  isOfferListing = false,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const node = React.useRef<HTMLDivElement>(null);
  
  useClickOutside(node, () => {
    onClose();
  });

  const buildStayText = (offerData: IOffers) => {
    let text = "";
    if (offerData?.arrival_date_gt && offerData?.departure_date_lt) {
      text = `${t("offers.fromTxt")} ${format(
        new Date(offerData?.arrival_date_gt),
        "LLL dd"
      )} ${t("offers.toText")} ${format(
        new Date(offerData.departure_date_lt),
        "LLL dd"
      )}`;
      text = offerData?.length_of_stay
        ? `${t("offers.losTxt1")} ${offerData.length_of_stay} ${t(
            "offers.losTxt3"
          )} ${text}`
        : `${t("offers.losTxt1")} ${text}`;
      return text;
    }
    if (offerData?.length_of_stay) {
      text = `${t("offers.losTxt1")} ${offerData.length_of_stay} ${t(
        "offers.losTxt2"
      )}`;
    }
    return text;
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="modal is-active offer-detail-modal">
      <div className="modal-background background-banner" onClick={onClose}></div>
      <div className="modal-window" ref={node as any}>
        <div className="modal-inner-wrapper">
          <button className="close-btn" onClick={onClose}>
            <img src={close} alt="close" />
          </button>
          
          <div className="deal-img" style={{ backgroundImage: `url(${offer.image || defaultImg})` }}>
            <p className="category">{isOfferListing ? t("offers.offerApplied") : t(OFFER_MAPPING[offer.type])}</p>
          </div>
          
          <div className="modal-deal-content">
            {formattedTitle(
              offer.title || t("offers.hurry"),
              offer.highlight_text,
              "deal-title",
              "deal-offer",
              false
            )}
            {formattedTitle(
              offer.description_1,
              offer.highlight_text,
              "deal-subtitle",
              "deal-offer",
              true
            )}
            
            {offer.description_2 ? (
              <div style={{ marginTop: "10px" }}>
                {formattedTitle(
                  offer.description_2,
                  offer.highlight_text,
                  "deal-subtitle",
                  "deal-offer",
                  true
                )}
              </div>
            ) :  null}
            
            {offer.description_3 ?  (
              <div className="long-description">
                <p className="long-desc-text">{offer.description_3}</p>
              </div>
            ) :  null}
            
            {((offer.arrival_date_gt && offer.departure_date_lt) ||
              offer.length_of_stay)  ? (
              <p className="desc-green">{buildStayText(offer)}</p>
            ) : null}
            
            {offer.expiry ? (
              <Timer expiryTimestamp={new Date(offer.expiry).getTime()} />
            ) : null}
            
            {offer.code ? (
              <div className="offer-code-section">
                <span className="code-label">{t("offers.code")}:</span>
                <span className="code-value">{offer.code}</span>
              </div>
            ) : null}
            
            <div className="modal-actions">
              <button
                className="main-button"
                onClick={() => routeToOfferDetails(offer, history, dispatch)}
                type="button"
                disabled={isOfferListing}
              >
                {isOfferListing ? t("offers.offerApplied") : t("offers.applyNow") || "Apply Now"}
              </button>
              <button
                className="outline-button"
                onClick={() => handleShareOffer(offer, t, dispatch)}
                type="button"
                title={t("offers.shareOffer")}
              >
                {t("offers.shareOffer") || "Share Offer"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default OfferDetailModal;
